import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';

const MENU_WIDTH = 220;

// SVG icons inline — sin dependencias externas
const IconShield = () => (
  <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);
const IconClock = () => (
  <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);
const IconUnlock = () => (
  <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 9.9-1" />
  </svg>
);
const IconUserCheck = () => (
  <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <polyline points="16 11 18 13 22 9" />
  </svg>
);
const IconUserX = () => (
  <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <line x1="17" y1="8" x2="23" y2="14" />
    <line x1="23" y1="8" x2="17" y2="14" />
  </svg>
);

const MenuItem = ({ onClick, icon, label, variant = 'default' }) => {
  const variants = {
    default: 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600/60',
    danger:  'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30',
    success: 'text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/30',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2.5 w-full text-left px-3 py-2 text-sm transition-colors ${variants[variant]}`}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
};

const SectionLabel = ({ children }) => (
  <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
    {children}
  </div>
);

const Divider = () => <div className="my-1 border-t border-gray-100 dark:border-gray-600/60" />;

const UserActionsMenu = ({
  usuario,
  permisoModeracion,
  toggleAcciones,
  accionesUsuario,
  setAccionMenu,
  handleActualizarRol,
  confirmarAccion,
}) => {
  const userId = usuario.id;
  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  const menuVisible = accionesUsuario[userId] || false;
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

  const puedeCambiarRol = permisoModeracion === 'admin';

  // Jerarquía: admin(3) > moderador(2) > usuario(1)
  // Solo se puede sancionar a alguien de rango estrictamente inferior
  const JERARQUIA = { admin: 3, moderador: 2, usuario: 1 };
  const puedeModerar =
    (JERARQUIA[permisoModeracion] || 0) > (JERARQUIA[usuario.rol] || 0);
  const isBannedActive =
    (usuario.baneado === 1 || usuario.baneado === '1' || usuario.baneado === true) &&
    (!usuario.fecha_baneo || new Date(usuario.fecha_baneo) > new Date());

  const calcularPosicion = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const menuHeight = menuRef.current?.offsetHeight || 320;
    const spaceBelow = window.innerHeight - rect.bottom;
    const top =
      spaceBelow >= menuHeight + 8 ? rect.bottom + 4 : rect.top - menuHeight - 4;
    const left = Math.min(rect.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8);
    setMenuPos({ top: Math.max(8, top), left: Math.max(8, left) });
  }, []);

  useEffect(() => {
    if (!menuVisible) return undefined;
    calcularPosicion();

    const handleOutside = (e) => {
      if (!buttonRef.current?.contains(e.target) && !menuRef.current?.contains(e.target)) {
        setAccionMenu(userId, false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    window.addEventListener('scroll', calcularPosicion, true);
    window.addEventListener('resize', calcularPosicion);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      window.removeEventListener('scroll', calcularPosicion, true);
      window.removeEventListener('resize', calcularPosicion);
    };
  }, [menuVisible, setAccionMenu, userId, calcularPosicion]);

  const close = () => setAccionMenu(userId, false);

  const menuContent = (
    <div
      ref={menuRef}
      style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, width: MENU_WIDTH, zIndex: 9999 }}
      className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl overflow-hidden"
    >
      {/* Cabecera usuario */}
      <div className="px-3 py-2.5 bg-gray-50 dark:bg-gray-700/60 border-b border-gray-200 dark:border-gray-700">
        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">
          {usuario.username}
        </p>
        <p className="text-xs text-gray-400 dark:text-gray-500 capitalize">{usuario.rol}</p>
      </div>

      {/* Sección cambiar rol */}
      {puedeCambiarRol && (
        <>
          <SectionLabel>Rol</SectionLabel>
          {usuario.rol !== 'moderador' && (
            <MenuItem
              icon={<IconUserCheck />}
              label="Hacer Moderador"
              onClick={() => { handleActualizarRol(usuario.id, 'moderador'); close(); }}
            />
          )}
          {usuario.rol !== 'prueba' && (
            <MenuItem
              icon={<IconUserCheck />}
              label="Hacer usuario de prueba"
              onClick={() => { handleActualizarRol(usuario.id, 'prueba'); close(); }}
            />
          )}
          {usuario.rol !== 'usuario' && (
            <MenuItem
              icon={<IconUserX />}
              label="Quitar privilegios"
              onClick={() => { handleActualizarRol(usuario.id, 'usuario'); close(); }}
            />
          )}
        </>
      )}

      {/* Sección moderación */}
      {puedeModerar && (
        <>
          {puedeCambiarRol && <Divider />}
          <SectionLabel>Sanciones</SectionLabel>

          {isBannedActive ? (
            <MenuItem
              icon={<IconUnlock />}
              label="Levantar sanción"
              variant="success"
              onClick={() => { confirmarAccion('desbanear', usuario.id); close(); }}
            />
          ) : (
            <>
              <MenuItem
                icon={<IconClock />}
                label="Timeout · 1 día"
                variant="danger"
                onClick={() => { confirmarAccion('banear', usuario.id, 1); close(); }}
              />
              <MenuItem
                icon={<IconClock />}
                label="Timeout · 5 días"
                variant="danger"
                onClick={() => { confirmarAccion('banear', usuario.id, 5); close(); }}
              />
              <MenuItem
                icon={<IconClock />}
                label="Timeout · 7 días"
                variant="danger"
                onClick={() => { confirmarAccion('banear', usuario.id, 7); close(); }}
              />
              <MenuItem
                icon={<IconShield />}
                label="Baneo permanente"
                variant="danger"
                onClick={() => { confirmarAccion('banear', usuario.id, null); close(); }}
              />
            </>
          )}
        </>
      )}

      {!puedeCambiarRol && !puedeModerar && (
        <p className="px-3 py-3 text-xs text-gray-400 dark:text-gray-500 italic">
          Sin acciones disponibles
        </p>
      )}

      <div className="h-1" />
    </div>
  );

  return (
    <div className="inline-block">
      <button
        ref={buttonRef}
        id={`acciones-btn-${usuario.id}`}
        type="button"
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); toggleAcciones(usuario.id); }}
        className="p-2.5 sm:p-1.5 inline-flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-gray-700 transition-colors"
        aria-label={`Acciones de ${usuario.username}`}
      >
        {/* Tres puntos vertical (SVG inline, sin red) */}
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
          <circle cx="12" cy="5" r="1.5" />
          <circle cx="12" cy="12" r="1.5" />
          <circle cx="12" cy="19" r="1.5" />
        </svg>
      </button>

      {menuVisible && createPortal(menuContent, document.body)}
    </div>
  );
};

export default UserActionsMenu;
